/* eslint-disable max-lines */
import { useEffect, useState, Fragment, useMemo } from 'react'
import { Box, Center, Divider, HStack, Stack, Text, VStack } from '@chakra-ui/react'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { DragDropContext, Droppable, Draggable } from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import { Row, Collapse } from 'reactstrap'
import { primary } from 'theme/globalStyles'
import { UpdateBody } from 'types/common'
import { uploadFile } from 'API/cms'
import { createMedia, updateMediaById } from 'API/media'
import { updateTextContentById } from 'API/textContent'
import Icon from 'components/Icon'
import ProcessMediaTab from 'components/ProcessMediaTab'
import SvgIcon from 'components/SvgIcon'
import { IMedia } from 'interfaces/media'
import { IStepWithRelations } from 'interfaces/step'
import { ITextContentWithRelations } from 'interfaces/textContent'
import { ITheme } from 'interfaces/theme'
import { checkValidArray, getValidArray } from 'utils/common'
import MediaManager, { MediaFileType, MEDIATYPES } from '../../../../MediaManager'
import { getRenderProcess } from '../../utils'
import BlockTextEditor from './components/BlockTextEditor'
import EmbedLinkModal from './components/BlockTextEditor/components/EmbedLinkModal'
import CardHeader from './components/CardHeader'
import DropZone from './components/DropZone'
import MediaView from './components/MediaView'
import TaskCard from './components/TaskCard'
import { OutlineButton, UploadButton } from './stepCard.styles'
import styles from './stepCard.module.scss'

interface IStepCardProps {
  lastStep: boolean
  step: IStepWithRelations
  isCommonStepFromStepId?: number
  onToggleMessageView?: Function
  procedureId: number
  isStartEditing: boolean
  isDragging: boolean
  setDisabledButtonPublishDraft: (value: number) => void
  handleShowAutosave: () => void
  canEditProcess: boolean
  searchText?: string
  alwayExpand?: boolean
  onActivity?: () => void
  onLockError?: (ownerName?: string) => void
  needsReview?: boolean
  changeType?: 'added' | 'modified' | 'reordered' | null
  isStepLevelChange?: boolean
  stepLevelChangeType?: 'added' | 'modified' | 'reordered' | null
  hideStepNumbers?: boolean
  isBlockHighlighted?: (stepId: number, blockId: number) => boolean
  getBlockChangeType?: (stepId: number, blockId: number) => 'added' | 'modified' | 'reordered' | null
  isReviewMode?: boolean
  reviewStatusMap?: Record<number, {needsReview: boolean; isReviewed: boolean}>
  onToggleReviewItem?: (blockId: number) => void
  stepReviewStatusMap?: Record<number, {needsReview: boolean; isReviewed: boolean}>
  onToggleStepReview?: (stepId: number) => void
}

const StepCard = ({
  step,
  lastStep,
  searchText,
  procedureId,
  isStartEditing,
  isDragging,
  setDisabledButtonPublishDraft,
  handleShowAutosave,
  onToggleMessageView = () => {},
  canEditProcess,
  alwayExpand,
  isCommonStepFromStepId,
  onActivity,
  onLockError,
  needsReview = false,
  changeType = null,
  isStepLevelChange = false,
  stepLevelChangeType = null,
  hideStepNumbers = true,
  isBlockHighlighted,
  getBlockChangeType,
  isReviewMode = false,
  reviewStatusMap = {},
  onToggleReviewItem = () => {},
  stepReviewStatusMap = {},
  onToggleStepReview = () => {}
}: IStepCardProps) => {
  const { processStore, organizationStore, iconBuilderStore, authStore } = useStores()
  // Reliable org id for uploads/media display: the org store may not be loaded
  // yet on a direct detail-page visit — fall back to the persisted active org
  // (resolvedActiveOrgId reads localStorage synchronously, so it works before
  // bootstrapAuthSession finishes; never 0 → uploadFile rejects instead of
  // misfiling under organizations/0/).
  const mediaOrgId = organizationStore.organization?.id ?? authStore.resolvedActiveOrgId ?? 0
  const [isEditing, setIsEditing] = useState(false)
  const [showEditStep, setShowEditStep] = useState(false)
  const [showAddTextBlock, setShowAddTextBlock] = useState<boolean>(false)
  const [isEditingMedia, setIsEditingMedia] = useState<boolean>(false)
  const [editingBlockText, setEditingBlockText] = useState<ITextContentWithRelations | null>(null)
  const [showManageMediaDialog, setShowManageMediaDialog] = useState(false)
  const [showEmbedLinkModal, setShowEmbedLinkModal] = useState<boolean>(false)

  const { expandingSteps } = processStore
  const isManageMode = processStore?.isManageModeInDetail

  const isOpen = expandingSteps?.includes(step.id) ?? false
  const [textContents, setTextContents] = useState<ITextContentWithRelations[]>([])
  const [sequenceChanged, setSequenceChanged] = useState(false)

  const mediaList: IMedia[] = step?.media || []
  const [selectedMedia, setSelectedMedia] = useState<IMedia>(mediaList[0] ? mediaList[0] : ({} as IMedia))
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}

  const hasMedia: boolean = checkValidArray(mediaList)
  const hasTextContent: boolean = checkValidArray(step?.textContents)

  useEffect(() => {
    setTextContents(step?.textContents ?? [])
    if (step?.icon) {
      iconBuilderStore.setSelectedIcon(step.icon)
    }

    if (alwayExpand) {
      processStore.setExpandingSteps([...expandingSteps, step.id])
    }
  }, [step])

  useEffect(() => {
    if (isStartEditing) {
      setIsEditing(true)
      setShowEditStep(false)
      !isOpen && toggleExpand()
    }
  })

  useEffect(() => {
    if (!isManageMode) {
      setIsEditing(false)
    }
  }, [isManageMode])

  async function handleFinishEditing(): Promise<void> {
    setIsEditing(false)
    setDisabledButtonPublishDraft(step.id)
    if (sequenceChanged) {
      await Promise.all(
        textContents.map((textContent, idx) => {
          return updateTextContentById(textContent.id, { position: idx + 1 })
        })
      )
      setSequenceChanged(false)
      if (step.processId) {
        getRenderProcess(step.processId, processStore)
      }
    }
  }

  const reorder = (
    list: ITextContentWithRelations[],
    startIndex: number,
    endIndex: number
  ): ITextContentWithRelations[] => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  function toggleExpand(): void {
    if (isOpen) {
      const currentStepIndex = expandingSteps?.findIndex(expandingId => expandingId === step.id) ?? -1
      let newExpandedSteps = [...(expandingSteps || [])]
      if (currentStepIndex >= 0) {
        newExpandedSteps.splice(currentStepIndex, 1)
      }
      processStore.setExpandingSteps(newExpandedSteps)
    } else {
      processStore.setExpandingSteps([...(expandingSteps || []), step.id])
    }
  }

  function handleOpenMediaManager(event: React.MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation()
    setShowManageMediaDialog(true)
  }

  function handleOpenEmbedLinkModal(event: React.MouseEvent<HTMLButtonElement>): void {
    event.stopPropagation()
    setShowEmbedLinkModal(true)
  }

  async function uploadMedia(fileToUpload: File, setUploadProgress: (percent: number) => any) {
    const category: any = fileToUpload.type.split('/')[0]
    const fileType = [MediaFileType.IMAGE, MediaFileType.VIDEO].includes(category) ? category : MediaFileType.FILE

    const url = await uploadFile(
      fileType,
      fileToUpload,
      (data: any) => {
        setUploadProgress(Math.round((100 * data.loaded) / data.total))
      },
      mediaOrgId
    )
    if (url) {
      const mediaBody: UpdateBody<IMedia> = {
        organizationId: organizationStore.organization?.id,
        position: mediaList.length + 1, // continue from latest position
        mediaType: MEDIATYPES[fileType].mediaType,
        stepId: step.id,
        createdAt: dayjs().toDate(),
        updatedAt: dayjs().toDate(),
        [MEDIATYPES[fileType].column]: url.split('/').pop(),
        originalFile: fileToUpload.name
      }

      await createMedia(mediaBody)
      //
      getRenderProcess(processStore.process.id, processStore)
    }
  }

  async function handleChangeCaption(mediaId: number, caption: string) {
    await updateMediaById(mediaId, { name: caption })
    //
    await getRenderProcess(processStore.process.id, processStore)
  }

  // Get step review status
  const stepReviewStatus = useMemo(() => {
    if (!isReviewMode || !step.id) return {needsReview: false, isReviewed: false}
    return stepReviewStatusMap[step.id] || {needsReview: false, isReviewed: false}
  }, [isReviewMode, step.id, stepReviewStatusMap])

  const {needsReview: stepNeedsReview, isReviewed: stepIsReviewed} = stepReviewStatus

  return (
    <Row
      className={cx(styles.container, isDragging && styles.isDragging)}
      style={{
        position: 'relative',
        border: isStepLevelChange && stepNeedsReview ? (stepIsReviewed ? '2px solid #48BB78' : '2px solid #F56565') : undefined,
        borderRadius: isStepLevelChange && stepNeedsReview ? '4px' : undefined
      }}
    >
      <CardHeader
        needsReview={needsReview}
        canEditProcess={canEditProcess}
        onClick={alwayExpand ? () => {} : toggleExpand}
        position={step.position}
        name={step.name}
        hideStepNumbers={hideStepNumbers}
        isEditing={isEditing}
        mediaList={mediaList}
        searchText={searchText}
        step={step}
        isCommonStepFromStepId={isCommonStepFromStepId}
        lastStep={lastStep}
        onStartEditing={() => {
          setIsEditing(true)
          setShowEditStep(false)
          !isOpen && toggleExpand()
          setDisabledButtonPublishDraft(step.id)
        }}
        onStopEditing={handleFinishEditing}
        onToggleMessageView={onToggleMessageView}
        procedureId={procedureId}
        handleShowAutosave={() => handleShowAutosave()}
        className={styles.cardHeader}
        isCollapsed={!isOpen}
        isStepLevelChange={isStepLevelChange}
        stepLevelChangeType={stepLevelChangeType}
        isReviewMode={isReviewMode}
        stepReviewStatus={stepReviewStatus}
        onToggleStepReview={onToggleStepReview}
      />
      {isEditing && !hasTextContent && !hasMedia && (
        <VStack width="full" padding={6} spacing={3}>
          <DropZone
            step={step}
            organizationId={mediaOrgId}
            fetchData={() => getRenderProcess(step?.processId ?? 0, processStore)}
          >
            <VStack>
              <Text color="gray.500" fontSize="sm" fontWeight={500} lineHeight={5}>
                Paste, drag or upload files.
              </Text>
              <HStack>
                <UploadButton fontSize="sm" onClick={handleOpenMediaManager}>
                  <SvgIcon iconName="file-upload" size={14} color="#4A5568" />
                  Upload files
                </UploadButton>
                <UploadButton fontSize="sm" onClick={handleOpenEmbedLinkModal}>
                  <SvgIcon iconName="insert-link" size={14} color="#4A5568" />
                  Embed link
                </UploadButton>
              </HStack>
            </VStack>
          </DropZone>
          <HStack spacing={3}>
            <Box width="42px" height="1px" background="gray.300" />
            <Text color="gray.500" fontWeight={500} lineHeight={6}>
              OR
            </Text>
            <Box width="42px" height="1px" background="gray.300" />
          </HStack>
          <OutlineButton
            width={{ base: 'full', md: 'initial' }}
            color={currentTheme?.primaryColor ?? primary}
            border={`1px solid ${currentTheme?.primaryColor ?? primary}`}
            _hover={{
              color: 'white',
              background: `${currentTheme?.primaryColor ?? primary}`
            }}
            onClick={() => setShowAddTextBlock(true)}
          >
            + Add new instruction
          </OutlineButton>
        </VStack>
      )}
      {hasTextContent || hasMedia ? (
        <Collapse className={styles.collapse} isOpen={isOpen}>
          <Stack
            width="full"
            height="full"
            align="flex-start"
            justify="space-between"
            borderTop="1px solid #E2E8F0"
            flexDirection={{ base: 'column', md: 'row' }}
            spacing={0}
          >
            <VStack width="full" align="flex-start" padding={{ base: 4, sm: 6 }} spacing={3}>
              {hasMedia && !hasTextContent ? (
                <VStack width="full" spacing={3}>
                  <MediaView
                    mediaList={mediaList}
                    setSelectedMedia={setSelectedMedia}
                    mediaIndex={getValidArray(mediaList).findIndex(media => media?.id === selectedMedia?.id)}
                    isEditingMedia={isEditingMedia}
                  />
                  {isEditing && (
                    <HStack spacing={3}>
                      <Box width="42px" height="1px" background="gray.300" />
                      <Text color="gray.500" fontWeight={500} lineHeight={6}>
                        OR
                      </Text>
                      <Box width="42px" height="1px" background="gray.300" />
                    </HStack>
                  )}
                </VStack>
              ) : (
                <DragDropContext
                  onDragEnd={async result => {
                    if (!result.destination) {
                      return
                    }
                    const items = reorder(textContents, result.source.index, result.destination.index)
                    setTextContents(items)
                    try {
                      await Promise.all(
                        items.map((textContent: ITextContentWithRelations, index: number) => 
                          updateTextContentById(textContent.id, { position: index + 1 })
                        )
                      )
                      onActivity?.()
                      setSequenceChanged(true)
                    } catch (err: any) {
                      const status = err?.response?.status
                      if (status === 403 || status === 409) {
                        const ownerName = err?.response?.data?.error?.details?.editLockOwnerName as string | undefined
                        onLockError?.(ownerName)
                        // Revert to original order on error
                        setTextContents(step?.textContents ?? [])
                      } else {
                        throw err
                      }
                    }
                  }}
                >
                  <Droppable droppableId="droppable" isDropDisabled={!isEditing}>
                    {(provided, snapshot) => (
                      <div
                        style={
                          snapshot.isDraggingOver
                            ? { background: '#EEE', height: '100%', width: '100%' }
                            : { width: '100%' }
                        }
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {Array.isArray(textContents) &&
                          textContents.map((textContent: ITextContentWithRelations, index: number) => (
                            <Fragment key={`textContent-${textContent.id}`}>
                              <Draggable
                                draggableId={`${textContent.id}`}
                                index={index}
                                isDragDisabled={!isEditing}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    ref={provided.innerRef}
                                    style={{
                                      ...provided.draggableProps.style,
                                      display: 'flex',
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      background: '#FFFFFF',
                                      boxShadow: snapshot.isDragging ? '0px 6px 6px rgba(0, 0, 0, 0.08)' : 'initial'
                                    }}
                                    className={isEditing ? styles.dragAbleBlock : undefined}
                                    onClick={() => {
                                      setShowEditStep(true)
                                      setEditingBlockText(textContent)
                                    }}
                                  >
                                    {isEditing && <Icon icon="grip-vertical" group="fontawesome" />}
                                    <div
                                      className={cx(styles.cardFullWidth, styles.cardSpacingRight, {
                                        [styles.cardSpacingLeft]: !isEditing
                                      })}
                                    >
                                      <TaskCard
                                        key={`taskCard-${textContent.id}`}
                                        task={textContent}
                                        isEditing={isEditing}
                                        mediaList={mediaList}
                                        selectedMedia={selectedMedia}
                                        setSelectedMedia={setSelectedMedia}
                                        needsReview={
                                          isBlockHighlighted && step.id && textContent.id
                                            ? isBlockHighlighted(step.id, Number(textContent.id))
                                            : false
                                        }
                                        changeType={
                                          getBlockChangeType && step.id && textContent.id
                                            ? getBlockChangeType(step.id, Number(textContent.id))
                                            : null
                                        }
                                        isReviewMode={isReviewMode}
                                        reviewStatusMap={reviewStatusMap}
                                        onToggleReviewItem={onToggleReviewItem}
                                        stepId={step.id}
                                      />
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                              {index < textContents.length - 1 && (
                                <Divider borderColor="gray.200" marginY="16px" width="100%" />
                              )}
                            </Fragment>
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              <Center width="full">
                {isEditing && (
                  <OutlineButton
                    width={{ base: 'full', md: 'initial' }}
                    color={currentTheme?.primaryColor ?? primary}
                    border={`1px solid ${currentTheme?.primaryColor ?? primary}`}
                    _hover={{
                      color: 'white',
                      background: `${currentTheme?.primaryColor ?? primary}`
                    }}
                    onClick={() => setShowAddTextBlock(true)}
                  >
                    + Add new instruction
                  </OutlineButton>
                )}
              </Center>
            </VStack>
            {!isReviewMode && (isEditing || (!isEditing && hasMedia)) && (
              <Box maxWidth={{ md: '212px' }} width="full" height="full" borderLeft="2px solid #E2E8F0">
                <ProcessMediaTab
                  isEditing={isEditing}
                  setShowManageMediaDialog={setShowManageMediaDialog}
                  mediaList={mediaList}
                  selectedMedia={selectedMedia}
                  setSelectedMedia={setSelectedMedia}
                  step={step}
                  hasTextContent={hasTextContent}
                  isOpenEmbedLinkModal={showEmbedLinkModal}
                  closeEmbedLinkModal={() => setShowEmbedLinkModal(false)}
                  isEditingMedia={isEditingMedia}
                  setIsEditingMedia={setIsEditingMedia}
                />
              </Box>
            )}
          </Stack>
        </Collapse>
      ) : (
        !isEditing && (
          <Collapse className={styles.collapse} isOpen={isOpen}>
            <Text margin={{ base: 4, sm: 6 }}>Empty drill</Text>
          </Collapse>
        )
      )}
      {isEditing && (
        <BlockTextEditor
          isOpen={showEditStep}
          toggle={() => setShowEditStep(!showEditStep)}
          stepId={step.id || 0}
          blockText={editingBlockText}
          step={step}
          isEditMode
          handleShowAutosave={() => handleShowAutosave()}
          handleShowManageMediaDialog={() => setShowManageMediaDialog(true)}
          handleShowEmbedLinkModal={() => setShowEmbedLinkModal(true)}
          onActivity={onActivity}
          onLockError={onLockError}
        />
      )}
      {showAddTextBlock && (
        <BlockTextEditor
          isOpen={showAddTextBlock}
          toggle={() => setShowAddTextBlock(false)}
          stepId={step.id || 0}
          step={step}
          isEditMode={false}
          handleShowAutosave={() => handleShowAutosave()}
          handleShowManageMediaDialog={() => setShowManageMediaDialog(true)}
          handleShowEmbedLinkModal={() => setShowEmbedLinkModal(true)}
          onActivity={onActivity}
          onLockError={onLockError}
        />
      )}
      <MediaManager
        onUpdateCaption={handleChangeCaption}
        onFileUpload={uploadMedia}
        toggle={() => setShowManageMediaDialog(!showManageMediaDialog)}
        isOpen={showManageMediaDialog}
        mediaList={mediaList}
        handleShowAutosave={() => handleShowAutosave()}
      />
      <EmbedLinkModal
        isOpen={showEmbedLinkModal}
        onClose={() => setShowEmbedLinkModal(false)}
        step={step}
        mediaList={mediaList}
      />
    </Row>
  )
}

export default observer(StepCard)
